import { useCallback, useEffect, useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import { useTranslations } from 'next-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { Skeleton } from 'src/app/components/Elements/Skeleton';
import { usePharmacySearch, usePlaceDetail } from 'src/app/hooks/data/pharmacy';
import { useGetHubPendingEscripts } from 'src/app/hooks/data/pharmacyHub';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import * as actionTypes from 'src/app/state/actions/types';
import { useLogRocketInit } from 'src/hooks/useLogRocketInit';

import { CLEARRX_PHONE } from '../constants';
import { HubInvalidCode } from '../HubInvalidCode';
import { ContentContainer } from '../styles';

import { ConfirmationSuccess } from './ConfirmationSuccess';
import { ConfirmPharmacy } from './ConfirmPharmacy';
import { PharmacyHubWelcome } from './PharmacyHubWelcome';

export enum PharmacyHubStep {
  welcome = 'welcome',
  confirm = 'confirm',
  success = 'success',
}

export type SelectedPharmacy = {
  name: string;
  address?: string;
  phone?: string;
};

export const ClearRxSelect = () => {
  const t = useTranslations('clearRxSelect');
  const [currentView, setCurrentView] = useState<PharmacyHubStep | null>(null);
  const [isLoginError, setLoginError] = useState<boolean>(false);
  const [isFinishedWithLogin, setIsFinishedWithLogin] = useState<boolean>(false);
  const [hasStartedLogin, setHasStartedLogin] = useState<boolean>(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<SelectedPharmacy>();
  const dispatch = useDispatch();
  const dispatchErrorEvent = useSegmentEvent('error', { shouldTrackOnMount: false });

  const { isLoggedIn, loggedInUser, logout, loginByCode } = useAuth();
  const { search, pathname } = useLocation();
  const history = useHistory();
  const { data: user, refetch: refetchUserData } = useUser(loggedInUser ? loggedInUser.uuid : '', false, 1, false);

  const params = new URLSearchParams(search);
  const currentStep = params.get('step');
  const code = params.get('code');
  const pendingEscriptId = params.get('entryId');
  const userUuid = params.get('userUuid');

  // Log rocket setup
  useLogRocketInit();
  useEffect(() => {
    if (user && isFinishedWithLogin) {
      try {
        LogRocket.identify(user.accountId);
      } catch (err) {
        console.error('Failed to identify user for LogRocket.', err);
      }
      dispatch({
        type: actionTypes.SEGMENT_IDENTIFY,
        accountId: user.accountId,
      });
    }
  }, [user, isFinishedWithLogin]);

  const {
    data: pendingEscripts,
    refetch: fetchHubEscripts,
    isFetching: isFetchingPendingEscripts,
    isError: isFetchPendingEscriptError,
  } = useGetHubPendingEscripts(pendingEscriptId ?? '', false);

  // Page navigation Setup
  const setUrlParamStep = (step: PharmacyHubStep) => {
    // This allows tracking with browser buttons
    const params = new URLSearchParams(search);
    params.set('step', step);
    if (step === PharmacyHubStep.success) {
      history.replace({ pathname: pathname, search: '?' + params.toString() });
    } else {
      history.push({ pathname: pathname, search: '?' + params.toString() });
    }
  };

  const updateViewToRender = useCallback(() => {
    // handle step logic once we get pending escripts
    if (isFetchingPendingEscripts || !isFinishedWithLogin) return;

    if ((pendingEscripts && pendingEscripts.selected_pharmacy.pharmacy_selected_at !== null) || selectedPharmacy) {
      setCurrentView(PharmacyHubStep.success);
    } else if (currentStep === PharmacyHubStep.success && !selectedPharmacy) {
      setCurrentView(PharmacyHubStep.welcome);
    } else if (currentStep && Object.values(PharmacyHubStep).includes(currentView as PharmacyHubStep)) {
      // Need this step to allow for tracking with browser navigation button
      setCurrentView(currentStep as PharmacyHubStep);
    } else {
      // Always show welcome page on non existing params or unrecognized params
      setCurrentView(PharmacyHubStep.welcome);
    }
  }, [currentView, pendingEscripts, currentStep, isFetchingPendingEscripts, isFinishedWithLogin]);

  useEffect(() => {
    updateViewToRender();
  }, [updateViewToRender]);

  // Login effects
  useEffect(() => {
    if (isLoggedIn && loggedInUser && userUuid !== loggedInUser.uuid) {
      logout();
    }
  }, [loggedInUser, isLoggedIn, userUuid, logout]);

  useEffect(() => {
    const login = async () => {
      if (isFinishedWithLogin || isLoginError || hasStartedLogin) {
        // Only allow logging in once
        return;
      }

      if (!code || !userUuid || !pendingEscriptId) {
        setLoginError(true);
        setIsFinishedWithLogin(true);
        return;
      }

      if (isLoggedIn === false && !hasStartedLogin) {
        try {
          setHasStartedLogin(true);
          await loginByCode(userUuid, code);
        } catch {
          setLoginError(true);
          setIsFinishedWithLogin(true);
        }
      }
    };
    login();
  }, [loggedInUser, isLoggedIn, isFinishedWithLogin, isLoginError, logout, hasStartedLogin]);

  useEffect(() => {
    if (isLoggedIn && loggedInUser && userUuid === loggedInUser.uuid) {
      // The user can get logged out on error and we retry login on submit
      // so only run this if we logged in an did not already fetch escripts
      setLoginError(false);
      setIsFinishedWithLogin(true);
      fetchHubEscripts();
      refetchUserData();
    }
  }, [loggedInUser, isLoggedIn, userUuid, refetchUserData]);

  useEffect(() => {
    if (isFinishedWithLogin) {
      if (!isLoggedIn) {
        dispatchErrorEvent({
          errorMessage: 'Unexpected Log out Event',
          errorSummary: 'User logged out midsession',
          api: null,
          errorCode: null,
          source: pathname,
        });
        setLoginError(true);
      } else {
        setLoginError(false);
      }
    }
  }, [isLoggedIn, isFinishedWithLogin]);

  // Get Previous pharmacy data
  const previousPharmacyCopy = Object.assign({}, pendingEscripts?.previous_pharmacy, {
    // Drop anything after a # in the pharmacy name to capture more results
    // ex: Walgreens drug store #12345 will not return a result and this format is common in the EMR
    name: (pendingEscripts?.previous_pharmacy?.name || '').split('#')[0],
  });

  delete previousPharmacyCopy['address2'];
  const previousPharmacySearchTerm = Object.values(previousPharmacyCopy).join(' ');

  const { data: previousPharmacyResults, isFetching: isFetchingPreviousPharmacy } = usePharmacySearch(
    previousPharmacySearchTerm,
    '',
    10000,
    { type: 'pharmacy' },
    'pharmacy_hub_previous_pharmacy_search',
    true
  );

  const { data: previousPharmacyData, isFetching: isFetchingPreviousDetails } = usePlaceDetail(
    // Imperfect pharmacy result matching but at least ensure the zip code and door number is in the result
    previousPharmacyResults?.data?.results.length &&
      previousPharmacyResults?.data?.results[0].formatted_address?.includes(previousPharmacyCopy?.zipcode || '') &&
      previousPharmacyResults?.data?.results[0].formatted_address?.startsWith(
        previousPharmacyCopy?.address1?.split(' ')[0] || ''
      )
      ? previousPharmacyResults?.data?.results[0].place_id
      : '',
    'pharmacy_hub_previous_pharmacy_details',
    true
  );

  // Handle pharmacy already selected from pending escript api
  useEffect(() => {
    if (
      !isFetchingPendingEscripts &&
      pendingEscripts &&
      pendingEscripts.selected_pharmacy.pharmacy_selected_at !== null
    ) {
      setSelectedPharmacy({
        name: pendingEscripts.selected_pharmacy.selected_pharmacy_name ?? '',
        address: pendingEscripts.selected_pharmacy.selected_pharmacy_address ?? '',
        phone: pendingEscripts.selected_pharmacy.selected_pharmacy_phone,
      });
      setUrlParamStep(PharmacyHubStep.success);
    }
  }, [pendingEscripts, isFetchingPendingEscripts]);

  // Render Views
  if (isLoginError) {
    return HubInvalidCode();
  }

  if (!isFinishedWithLogin) {
    return <Skeleton />;
  }

  if (isFetchPendingEscriptError) {
    Sentry.captureException('Error fetching pharmacy hub entry data', {
      contexts: {
        request: {
          pendingEscriptId: pendingEscriptId,
        },
      },
    });

    // Split the error message on the {phone} placeholder
    const messageParts = t('errorMessage').split('{phone}');

    return (
      <MarketingPageSection>
        <ContentContainer>
          <Typography>
            {messageParts[0]}
            <a href={`tel:${CLEARRX_PHONE}`}>{CLEARRX_PHONE}</a>
            {messageParts[1]}
          </Typography>
        </ContentContainer>
      </MarketingPageSection>
    );
  }

  if (isFetchingPendingEscripts || !pendingEscripts || !pendingEscriptId) {
    return <Skeleton />;
  }

  if (pendingEscripts && pendingEscripts.medications.length === 0) {
    return (
      <MarketingPageSection>
        <ContentContainer>
          <Typography>{t('noPrescrptions')}</Typography>
        </ContentContainer>
      </MarketingPageSection>
    );
  }

  if (currentView == PharmacyHubStep.confirm) {
    return (
      <ConfirmPharmacy
        setSelectedPharmacy={setSelectedPharmacy}
        pendingEscriptId={pendingEscriptId}
        pendingEscripts={pendingEscripts}
        isFetchingPreviousPharmacyDetails={isFetchingPreviousPharmacy || isFetchingPreviousDetails}
        previousPharmacyGooglePlaceDetails={previousPharmacyData}
        refetchHubEscriptsOnSuccess={fetchHubEscripts}
      />
    );
  }

  if (currentView == PharmacyHubStep.success && selectedPharmacy) {
    return <ConfirmationSuccess selectedPharmacy={selectedPharmacy} pendingEscripts={pendingEscripts} />;
  }

  if (currentView == PharmacyHubStep.welcome) {
    return (
      <PharmacyHubWelcome
        handleStartConfirmPharmacyClick={() => setUrlParamStep(PharmacyHubStep.confirm)}
        pendingEscripts={pendingEscripts}
      />
    );
  }

  return <Skeleton />;
};
